import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Grid from '@config/Grid/Grid'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import Tile from '@/shared/components/Tile/Tile'
import Button from '@/shared/components/Button/Button'
import IconButton from '@/shared/components/IconButton/IconButton'
import Tabs from '@/shared/components/Tabs/Tabs'
import Select from '@/shared/components/Select/Select'
import Alert from '@/shared/components/Alert/Alert'
import RosterGrid from '@/features/employee-schedule/components/RosterGrid/RosterGrid'
import WeekCalendar from '@/features/employee-schedule/components/WeekCalendar/WeekCalendar'
import DayCalendar from '@/features/employee-schedule/components/DayCalendar/DayCalendar'
import MonthCalendar from '@/features/employee-schedule/components/MonthCalendar/MonthCalendar'
import ShiftModal from '@/features/employee-schedule/components/ShiftModal/ShiftModal'
import ScheduleSaveStatus from '@/features/employee-schedule/components/ScheduleSaveStatus/ScheduleSaveStatus'
import PayrollSummary from '@/features/employee-schedule/components/PayrollSummary/PayrollSummary'
import NightWindowFields from '@/features/employee-schedule/components/NightWindowFields/NightWindowFields'
import ScheduleExportModal from '@/features/employee-schedule/components/ScheduleExportModal/ScheduleExportModal'
import { useScheduleStore } from '@/features/employee-schedule/store/useScheduleStore'
import { useSchedule } from '@/features/employee-schedule/hooks/useSchedule'
import { useScheduleWeeksQuery } from '@/features/employee-schedule/hooks/useScheduleQuery'
import { usePayrollRows } from '@/features/employee-schedule/hooks/usePayrollRows'
import { monthRange } from '@/features/employee-schedule/utils/payroll'
import {
  CalendarMode,
  ScheduleView,
  Shift,
  ShiftDraft
} from '@/features/employee-schedule/types/schedule.model'
import {
  MINUTES_IN_HOUR,
  SHIFT_TEMPLATES,
  dayjs,
  findConflictingShift,
  formatMinutes,
  startOfWeek,
  toDateKey,
  weekStartsCovering
} from '@/features/employee-schedule/utils/schedule'
import styles from './EmployeeSchedule.module.scss'

/** Default length of a shift started by clicking an empty calendar slot. */
const DEFAULT_SHIFT_HOURS = 8

function EmployeeSchedule() {
  const { t, i18n } = useTranslation()
  const { showSnackbar } = useSnackbar()

  const [anchorDay, setAnchorDay] = useState(() => dayjs())
  const [view, setView] = useState<ScheduleView>('grid')
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('week')
  const [draft, setDraft] = useState<ShiftDraft | null>(null)
  const [isExportOpen, setIsExportOpen] = useState(false)

  const {
    days,
    rows,
    employees,
    shiftsForDay,
    employeeName,
    employeeColor,
    employeesLoading
  } = useSchedule(anchorDay)

  const { addShift, updateShift, removeShift, copyWeek, seedDemoShifts } =
    useScheduleStore()
  const allShifts = useScheduleStore(state => state.shifts)

  // Demo roster on first open, without it the grid is an empty week and it
  // isn't obvious the feature works at all.
  useEffect(() => {
    seedDemoShifts(employees.map(employee => employee.userId))
  }, [employees, seedDemoShifts])

  const weekStartKey = useMemo(
    () => toDateKey(startOfWeek(anchorDay)),
    [anchorDay]
  )

  // The payroll view reports a calendar month
  const payrollRange = useMemo(() => monthRange(anchorDay), [anchorDay])

  // The calendar's month mode covers the same span as payroll, and for the same
  // reason: the store holds only the weeks that have been fetched, so a month
  // read off a single hydrated week would show empty days that are not empty.
  const isCalendarMonth = view === 'calendar' && calendarMode === 'month'
  const isMonthSpan = view === 'payroll' || isCalendarMonth

  // Payroll summarises a month, so it steps and reads by month; the two
  // editing views work a week at a time.
  const isPayrollView = view === 'payroll'

  // The editing views only need the week on screen. Payroll reports a whole
  // month, and the store holds only the weeks that have been fetched, read
  // off a single hydrated week, a month would understate everyone's hours.
  const weeksToLoad = useMemo(
    () =>
      isMonthSpan
        ? weekStartsCovering(payrollRange.from, payrollRange.to)
        : [weekStartKey],
    [isMonthSpan, payrollRange, weekStartKey]
  )

  // Pulls those weeks from the backend when the API is enabled; a no-op
  // otherwise, so the offline-first mode is unaffected.
  const {
    isLoading: weekLoading,
    failedWeeks,
    isForbidden
  } = useScheduleWeeksQuery(weeksToLoad)

  const isLoading = employeesLoading || weekLoading

  const {
    rows: payrollRows,
    missingRate,
    withPremiums,
    ratesForbidden,
    isLoading: payrollLoading
  } = usePayrollRows(payrollRange, employees, allShifts)

  const calendarModeOptions = useMemo(
    () => [
      { label: t('SCHEDULE.MODE.DAY'), value: 'day' },
      { label: t('SCHEDULE.MODE.WEEK'), value: 'week' },
      { label: t('SCHEDULE.MODE.MONTH'), value: 'month' }
    ],
    [t]
  )

  const rangeLabel = useMemo(() => {
    if (isMonthSpan) return anchorDay.locale(i18n.language).format('MMMM YYYY')

    if (view === 'calendar' && calendarMode === 'day') {
      return anchorDay.locale(i18n.language).format('dddd, D MMMM YYYY')
    }

    const first = days[0]
    const last = days[days.length - 1]
    return `${first.format('D.MM')} – ${last.format('D.MM.YYYY')}`
  }, [isMonthSpan, view, calendarMode, anchorDay, days, i18n.language])

  // The arrows step by whatever is on screen, a month, a single day, or the
  // week every other view works in.
  const step = (direction: 1 | -1) =>
    setAnchorDay(current => {
      if (isMonthSpan) return current.add(direction, 'month')
      if (view === 'calendar' && calendarMode === 'day') {
        return current.add(direction, 'day')
      }
      return current.add(direction * 7, 'day')
    })

  const stepBack = () => step(-1)
  const stepForward = () => step(1)

  const handleAddShift = (userId: string, date: string) => {
    const [template] = SHIFT_TEMPLATES
    setDraft({
      userId,
      date,
      startMinutes: template.startMinutes,
      endMinutes: template.endMinutes
    })
  }

  // From the calendar the time is fixed but the person isn't, so the modal
  // opens with the employee field empty and waits for a pick. A swept-out
  // range supplies both ends; a plain click only the start.
  const handleAddShiftAtTime = (
    date: string,
    startMinutes: number,
    endMinutes?: number
  ) => {
    setDraft({
      userId: '',
      date,
      startMinutes,
      endMinutes:
        endMinutes ??
        Math.min(
          startMinutes + DEFAULT_SHIFT_HOURS * MINUTES_IN_HOUR,
          24 * MINUTES_IN_HOUR
        )
    })
  }

  const handleSaveShift = (saved: ShiftDraft) => {
    if (saved.id) {
      updateShift(saved.id, saved)
    } else {
      addShift(saved)
    }
    setDraft(null)
  }

  const handleDeleteShift = (id: string) => {
    removeShift(id)
    setDraft(null)
  }

  // Dropping a dragged shift writes straight through, no modal, like Teams.
  // Except onto another shift of the same person: nobody works two places at
  // once, and a drop that lands on one is refused. The drag hook has already
  // cleared its preview by now, so returning is the whole revert; the toast is
  // what stops that from reading as the app dropping the gesture.
  const handleMoveShift = (shift: Shift) => {
    const conflict = findConflictingShift(allShifts, shift)

    if (conflict) {
      showSnackbar(
        t('ALERTS.SHIFT_OVERLAP.TITLE'),
        t('ALERTS.SHIFT_OVERLAP.DESCRIPTION', {
          name: employeeName(shift.userId),
          from: formatMinutes(conflict.startMinutes),
          to: formatMinutes(conflict.endMinutes)
        }),
        'error'
      )
      return
    }

    updateShift(shift.id, {
      date: shift.date,
      startMinutes: shift.startMinutes,
      endMinutes: shift.endMinutes
    })
  }

  return (
    <Grid>
      <div className={`${styles.scheduleWrapper} start-2 col-11`}>
        <Tile color="black">
          <div className={styles.scheduleContainer}>
            <div className={styles.header}>
              <p className={`title-bold-medium ${styles.title}`}>
                {t('SCHEDULE.TITLE')}
              </p>
              <p className={`body-medium ${styles.description}`}>
                {t('SCHEDULE.DESCRIPTION')}
              </p>
            </div>

            {/* Wrapped rather than given an id: `Tabs` takes no id prop, so
                one set on it would never reach the DOM the tour looks in. */}
            <div id="tour-schedule-views">
              <Tabs
                tabs={[
                  { label: t('SCHEDULE.VIEW.GRID'), value: 'grid' },
                  { label: t('SCHEDULE.VIEW.CALENDAR'), value: 'calendar' },
                  { label: t('SCHEDULE.VIEW.PAYROLL'), value: 'payroll' }
                ]}
                value={view}
                onChange={value => setView(value as ScheduleView)}
              />
            </div>

            <div id="tour-schedule-toolbar" className={styles.toolbar}>
              <div className={styles.rangeNav}>
                <IconButton
                  icon="las la-angle-left icon-medium"
                  color="primary"
                  onClick={stepBack}
                />
                <span className={`body-bold-medium ${styles.rangeLabel}`}>
                  {rangeLabel}
                </span>
                <IconButton
                  icon="las la-angle-right icon-medium"
                  color="primary"
                  onClick={stepForward}
                />
                {/* Belongs with the date it moves, not with the actions. */}
                <Button
                  label={t('SCHEDULE.TODAY')}
                  variant="text"
                  color="primary"
                  onClick={() => setAnchorDay(dayjs())}
                />
              </div>

              {view === 'calendar' && (
                <Select
                  options={calendarModeOptions}
                  // The clear icon is hidden because there is no "no mode" -
                  // clearing it would leave the calendar with nothing to draw.
                  hideClearIcon
                  // Not `widthFitContent`: that sets the control to
                  // `fit-content`, and an Autocomplete's input has no intrinsic
                  // content width, so it collapsed onto the chevron and clipped
                  // the mode to a letter. A fixed width instead, wide enough for
                  // the longest label plus the component's own 4rem icon
                  // gutter, so the toolbar does not reflow when the mode
                  // changes.
                  minWidth="10rem"
                  maxWidth="10rem"
                  value={
                    calendarModeOptions.find(
                      option => option.value === calendarMode
                    ) ?? null
                  }
                  onChange={value => {
                    if (!value || Array.isArray(value)) return
                    setCalendarMode(value.value as CalendarMode)
                  }}
                />
              )}

              <div className={styles.toolbarActions}>
                {/* Payroll only reads the roster, editing actions have no
                    business being on screen there. */}
                {!isPayrollView && (
                  <Button
                    label={t('SCHEDULE.COPY_TO_NEXT_WEEK')}
                    variant="text"
                    color="primary"
                    icon="las la-copy"
                    onClick={() => copyWeek(weekStartKey)}
                  />
                )}
                {isPayrollView && <NightWindowFields />}
                <Button
                  label={t('SCHEDULE.EXPORT')}
                  variant="text"
                  color="primary"
                  icon="las la-file-export"
                  onClick={() => setIsExportOpen(true)}
                />
                {/*
                  Always mounted, payroll included: it carries the autosave
                  engine, and taking it off screen mid-write would drop the
                  write it was in the middle of.
                */}
                <ScheduleSaveStatus />
              </div>
            </div>

            {/*
              A week whose fetch failed is not an empty week. Saying so is the
              difference between a total the manager can trust and one that is
              plausibly, invisibly short.
            */}
            {!!failedWeeks.length && (
              <Alert
                severity="error"
                message={t(
                  isForbidden
                    ? 'SCHEDULE.WEEKS_FORBIDDEN'
                    : 'SCHEDULE.WEEKS_FAILED'
                )}
                onClose={() => undefined}
              />
            )}

            {view === 'payroll' && (
              <PayrollSummary
                isIncomplete={!!failedWeeks.length}
                ratesForbidden={ratesForbidden}
                rows={payrollRows}
                missingRate={missingRate}
                withPremiums={withPremiums}
                employeeColor={employeeColor}
                isLoading={isLoading || payrollLoading}
              />
            )}

            {view === 'grid' && (
              <RosterGrid
                days={days}
                rows={rows}
                onAddShift={handleAddShift}
                onEditShift={(shift: Shift) => setDraft(shift)}
                employeeColor={employeeColor}
                isLoading={isLoading}
              />
            )}

            {view === 'calendar' && calendarMode === 'day' && (
              <DayCalendar
                day={anchorDay}
                employees={employees}
                shifts={shiftsForDay(anchorDay)}
                employeeColor={employeeColor}
                onEditShift={(shift: Shift) => setDraft(shift)}
                // Same default length the week view gives a clicked slot, and
                // the same clamp at midnight, the endpoint rejects a shift
                // running past it.
                onAddShift={(userId, date, startMinutes) =>
                  setDraft({
                    userId,
                    date,
                    startMinutes,
                    endMinutes: Math.min(
                      startMinutes + DEFAULT_SHIFT_HOURS * MINUTES_IN_HOUR,
                      24 * MINUTES_IN_HOUR
                    )
                  })
                }
                isLoading={isLoading}
              />
            )}

            {view === 'calendar' && calendarMode === 'month' && (
              <MonthCalendar
                month={anchorDay}
                shiftsForDay={shiftsForDay}
                employeeColor={employeeColor}
                employeeName={employeeName}
                // Picking a day is the month view's whole interaction: it says
                // which days are busy, and the day view says by whom.
                onSelectDay={day => {
                  setAnchorDay(day)
                  setCalendarMode('day')
                }}
                isLoading={isLoading}
              />
            )}

            {view === 'calendar' && calendarMode === 'week' && (
              <WeekCalendar
                days={days}
                shiftsForDay={shiftsForDay}
                employeeName={employeeName}
                employeeColor={employeeColor}
                onEditShift={(shift: Shift) => setDraft(shift)}
                onAddShift={handleAddShiftAtTime}
                onMoveShift={handleMoveShift}
                isLoading={isLoading}
              />
            )}
          </div>
        </Tile>
      </div>

      <ScheduleExportModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        month={anchorDay}
        employees={employees}
        shifts={allShifts}
      />

      <ShiftModal
        isOpen={!!draft}
        draft={draft}
        employees={employees}
        findConflict={candidate => findConflictingShift(allShifts, candidate)}
        onClose={() => setDraft(null)}
        onSave={handleSaveShift}
        onDelete={handleDeleteShift}
      />
    </Grid>
  )
}

export default EmployeeSchedule
